import { useEffect, useMemo, useRef, useState } from "react";

interface Parsed {
  prefix: string;
  number: number;
  decimals: number;
  hasThousandsSep: boolean;
  suffix: string;
}

function parseValue(raw: string): Parsed | null {
  const match = raw.match(/^([^\d-]*)(-?[\d,]+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const [, prefix, numStr, suffix] = match;
  const hasThousandsSep = numStr.includes(",");
  const cleaned = numStr.replace(/,/g, "");
  const number = parseFloat(cleaned);
  if (Number.isNaN(number)) return null;
  const dot = cleaned.indexOf(".");
  const decimals = dot === -1 ? 0 : cleaned.length - dot - 1;
  return { prefix, number, decimals, hasThousandsSep, suffix };
}

function format(parsed: Parsed, current: number): string {
  const rounded =
    parsed.decimals === 0
      ? Math.round(current)
      : Number(current.toFixed(parsed.decimals));
  let numStr: string;
  if (parsed.hasThousandsSep) {
    numStr = rounded.toLocaleString("en-AU", {
      minimumFractionDigits: parsed.decimals,
      maximumFractionDigits: parsed.decimals,
    });
  } else {
    numStr = rounded.toFixed(parsed.decimals);
  }
  return `${parsed.prefix}${numStr}${parsed.suffix}`;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface AnimatedNumberProps {
  value: string;
  durationMs?: number;
  className?: string;
  once?: boolean;
}

/**
 * Animates a stat value string ("$166.8M", "84,861", "$80k+", "~15%", "$80,000")
 * from 0 to its parsed number when scrolled into view. Falls back to the
 * static value when the user prefers reduced motion, IntersectionObserver
 * isn't available, or the value can't be parsed.
 */
export function AnimatedNumber({
  value,
  durationMs = 1600,
  className,
  once = true,
}: AnimatedNumberProps) {
  const parsed = useMemo(() => parseValue(value), [value]);
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = useMemo(prefersReducedMotion, []);

  const [display, setDisplay] = useState<string>(() => {
    if (!parsed) return value;
    // Reduced-motion or no IO support → show final value immediately, no 0-flash.
    if (reduced || typeof IntersectionObserver === "undefined") {
      return format(parsed, parsed.number);
    }
    return format(parsed, 0);
  });
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!parsed) return;
    if (reduced) return;
    if (typeof IntersectionObserver === "undefined") return;

    const node = ref.current;
    if (!node) return;

    let rafId: number | null = null;
    let startTime: number | null = null;

    const animate = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(elapsed / durationMs, 1);
      const eased = easeOutCubic(t);
      setDisplay(format(parsed, parsed.number * eased));
      if (t < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        rafId = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (once && hasAnimatedRef.current) {
            observer.disconnect();
            return;
          }
          hasAnimatedRef.current = true;
          startTime = null;
          if (rafId !== null) cancelAnimationFrame(rafId);
          setDisplay(format(parsed, 0));
          rafId = requestAnimationFrame(animate);
          if (once) observer.disconnect();
        }
      },
      // Fire as soon as the element is within ~60% of viewport height of
      // entering — so on a phone, by the time the user has scrolled it into
      // actual view, the count-up has already finished.
      { threshold: 0, rootMargin: "0px 0px 60% 0px" },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // `parsed` is memoised by `value`, so listing `value` is enough.
  }, [value, parsed, durationMs, once, reduced]);

  if (!parsed) {
    return (
      <span ref={ref} className={className}>
        {value}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={className}
      aria-label={value}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {display}
    </span>
  );
}
